import axios from 'App/axios';
import { fileToBase64 } from 'commons/utils/fileToBase64';
import {
  IndividualOnboardingLinkResponse,
  IRecipient,
  RecipientErrorType,
  UploadDocumentResponse,
  UploadDocumentType,
} from '../types';

const recipientUrl = `admin/kyc/recipients/:recipientId`;

export const getRecipient = async (storeId: string): Promise<IRecipient> => {
  try {
    const { data } = await axios.get(
      recipientUrl.replace(':recipientId', storeId),
    );
    return data;
  } catch (error) {
    const err = error as RecipientErrorType;
    throw new Error(err?.response?.data?.error ?? 'defaultRecipientError');
  }
};

export const updateRecipient = async (
  storeId: string,
  recipient: IRecipient,
): Promise<IRecipient> => {
  try {
    const { data } = await axios.patch(
      recipientUrl.replace(':recipientId', storeId),
      {
        ...recipient,
        kyc: {
          ...recipient.kyc,
          tosAcceptanceIp: axios.defaults.headers.common['X-Forwarded-For'],
          tosAcceptanceDate: new Date().toISOString(),
        },
      },
    );
    return data;
  } catch (error: unknown) {
    const err = error as RecipientErrorType;
    throw new Error(err?.response?.data?.error ?? 'defaultRecipientError');
  }
};

// Persists the form without submitting it to the provider, so it must not
// record ToS acceptance.
export const saveRecipientDraft = async (
  storeId: string,
  recipient: IRecipient,
): Promise<IRecipient> => {
  try {
    const { data } = await axios.patch(
      `${recipientUrl.replace(':recipientId', storeId)}/draft`,
      recipient,
    );
    return data;
  } catch (error: unknown) {
    const err = error as RecipientErrorType;
    throw new Error(err?.response?.data?.error ?? 'defaultRecipientError');
  }
};

export const uploadDocuments = async (
  recipientId: string,
  documentsData: { front: File | null; back?: File | null },
  type: UploadDocumentType,
): Promise<UploadDocumentResponse> => {
  const body: any = { type };

  if (documentsData.front) {
    const frontBase64 = await fileToBase64(documentsData.front);
    body.front = {
      fileContent: frontBase64,
      mimeType: documentsData.front.type,
    };
  }
  if (documentsData.back) {
    const backBase64 = await fileToBase64(documentsData.back);
    body.back = {
      fileContent: backBase64,
      mimeType: documentsData.back.type,
    };
  }

  const url = `${recipientUrl.replace(':recipientId', recipientId)}/documents`;
  const response = await axios.post(url, body);
  if (response.status === 200 || response.status === 201) {
    return response.data;
  }
  throw new Error('Failed to upload documents');
};

export const getOnboardingLink = async (
  recipientId: string,
): Promise<IndividualOnboardingLinkResponse> => {
  const { data } = await axios.get(
    `${recipientUrl.replace(':recipientId', recipientId)}/onboarding`,
  );
  return data;
};

export default {
  getOnboardingLink,
  getRecipient,
  saveRecipientDraft,
  updateRecipient,
  uploadDocuments,
};
